import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors anywhere below it in the tree. Without this,
 * React 18 unmounts the whole root on an uncaught error, leaving a silent
 * blank white page with no indication anything went wrong.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled render error:', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg-2, #f5f7fb)',
          padding: 24,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: '100%',
            background: '#fff',
            border: '1px solid var(--line, #e2e8f0)',
            borderRadius: 12,
            padding: 32,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: 'var(--danger-2, #fee2e2)',
              color: 'var(--danger, #dc2626)',
              display: 'grid',
              placeItems: 'center',
              margin: '0 auto 16px',
              fontSize: 24,
            }}
          >
            !
          </div>
          <h1 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>Something went wrong</h1>
          <p style={{ fontSize: 13.5, color: 'var(--ink-4, #64748b)', margin: '0 0 20px' }}>
            This page hit an unexpected error. Reloading usually fixes it.
          </p>
          {import.meta.env.DEV && (
            <pre
              style={{
                textAlign: 'left',
                fontSize: 11.5,
                background: 'var(--bg-2, #f5f7fb)',
                border: '1px solid var(--line, #e2e8f0)',
                borderRadius: 8,
                padding: 12,
                marginBottom: 20,
                overflow: 'auto',
                maxHeight: 200,
                color: 'var(--danger, #dc2626)',
              }}
            >
              {error.message}
              {'\n'}
              {error.stack}
            </pre>
          )}
          <button
            type="button"
            onClick={this.handleReset}
            style={{
              padding: '10px 24px',
              background: 'var(--ink, #0f172a)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
